import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum NavigationSource {
  TOP_WINDOW = 'TOP_WINDOW',
  IFRAME = 'IFRAME',
  BROWSER_HISTORY = 'BROWSER_HISTORY',
  INITIAL = 'INITIAL',
}

export enum NavigationPhase {
  IDLE = 'IDLE',
  INITIATED = 'INITIATED',
  LOADING = 'LOADING',
  APPLYING_STYLES = 'APPLYING_STYLES',
  READY = 'READY',
  ERROR = 'ERROR',
}

export interface NavigationState {
  phase: NavigationPhase;
  source: NavigationSource;
  targetUrl: string | null;
  timestamp: number;
  lockId: string | null;
  iframeReady: boolean;
  stylesApplied: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationStateManager {
  private stateSubject = new BehaviorSubject<NavigationState>({
    phase: NavigationPhase.IDLE,
    source: NavigationSource.INITIAL,
    targetUrl: null,
    timestamp: Date.now(),
    lockId: null,
    iframeReady: false,
    stylesApplied: false,
  });

  public state$: Observable<NavigationState> = this.stateSubject.asObservable();

  private lastSyncedUrl: string | null = null;
  private navigationLockTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly LOCK_DURATION = 600; // ms
  private readonly LOG_PREFIX = '[NavStateManager]';

  constructor() {
    // TODO: Debug mode logs
    // Subscribe to state changes for logging
    // this.state$.subscribe((state) => {
    //   this.log(`State changed:`, {
    //     phase: state.phase,
    //     source: state.source,
    //     targetUrl: state.targetUrl,
    //     lockId: state.lockId,
    //     iframeReady: state.iframeReady,
    //     stylesApplied: state.stylesApplied,
    //   });
    // });
  }

  get currentState(): NavigationState {
    return this.stateSubject.value;
  }

  get isLocked(): boolean {
    return this.currentState.lockId !== null;
  }

  get lastSyncedRoute(): string | null {
    return this.lastSyncedUrl;
  }

  /**
   * Initiate navigation from top window to iframe
   */
  initiateTopWindowNavigation(targetUrl: string): string | null {
    const current = this.currentState;

    // Prevent duplicate navigation to same URL
    if (this.lastSyncedUrl === targetUrl && current.phase !== NavigationPhase.ERROR) {
      return null;
    }

    // If locked by another navigation, reject unless it's been too long
    if (this.isLocked && Date.now() - current.timestamp < 2000) {
      // Debug logs
      this.warn('Navigation locked, rejecting request to:', targetUrl);
      return null;
    }

    const lockId = this.generateLockId();
    this.updateState({
      phase: NavigationPhase.INITIATED,
      source: NavigationSource.TOP_WINDOW,
      targetUrl,
      timestamp: Date.now(),
      lockId,
      iframeReady: false,
      stylesApplied: false,
    });

    this.setNavigationLock(lockId);
    return lockId;
  }

  /**
   * Handle navigation initiated from iframe
   */
  handleIframeNavigation(iframeUrl: string): boolean {
    const current = this.currentState;

    // If we initiated this navigation from top window, ignore iframe feedback
    if (
      this.isLocked &&
      current.source === NavigationSource.TOP_WINDOW &&
      Date.now() - current.timestamp < 1000
    ) {
      return false;
    }

    // Check if this is actually a new navigation
    if (this.lastSyncedUrl === iframeUrl) {
      return false;
    }

    this.updateState({
      phase: NavigationPhase.INITIATED,
      source: NavigationSource.IFRAME,
      targetUrl: iframeUrl,
      timestamp: Date.now(),
      lockId: this.generateLockId(),
      iframeReady: false,
      stylesApplied: false,
    });

    return true;
  }

  /**
   * Mark iframe as loaded
   */
  markIframeLoaded(url: string): void {
    const current = this.currentState;

    if (current.targetUrl !== url && this.lastSyncedUrl !== url) {
      // Debug logs
      this.warn('Iframe loaded with unexpected URL:', url, 'expected:', current.targetUrl);
    }

    // CRITICAL: Only transition to APPLYING_STYLES if not already READY
    // When using location.go(), navigation completes before iframe load event fires
    // In that case, stay in READY state, just update iframeReady flag
    const newPhase = current.phase === NavigationPhase.READY
      ? NavigationPhase.READY
      : NavigationPhase.APPLYING_STYLES;

    this.updateState({
      ...current,
      phase: newPhase,
      iframeReady: true,
    });
  }

  /**
   * Mark styles as successfully applied
   */
  markStylesApplied(success: boolean): void {
    const current = this.currentState;

    if (success) {
      this.updateState({
        ...current,
        phase: NavigationPhase.READY,
        stylesApplied: true,
      });

      if (current.targetUrl) {
        this.lastSyncedUrl = current.targetUrl;
      }

      // Release lock after successful completion
      this.releaseLock();
    } else {
      // Debug logs
      this.error('Styles application failed');
      this.updateState({
        ...current,
        phase: NavigationPhase.ERROR,
        stylesApplied: false,
      });
    }
  }

  /**
   * Update phase (for loading, etc.)
   */
  updatePhase(phase: NavigationPhase): void {
    this.updateState({
      ...this.currentState,
      phase,
      timestamp: Date.now(),
    });
  }

  /**
   * Reset to idle state
   * CRITICAL: Also clears lastSyncedUrl to prevent blocking future navigation to the same URL
   * This is needed when navigating from an iframe page to a native Angular page and back
   */
  reset(): void {
    this.clearNavigationLock();
    // CRITICAL: Clear lastSyncedUrl so navigation to the same URL is allowed after reset
    // Without this, navigating /templates -> /categories -> /templates would be blocked
    // because shouldAllowNavigation checks lastSyncedUrl === url before phase
    this.lastSyncedUrl = null;
    this.updateState({
      phase: NavigationPhase.IDLE,
      source: NavigationSource.INITIAL,
      targetUrl: null,
      timestamp: Date.now(),
      lockId: null,
      iframeReady: false,
      stylesApplied: false,
    });
  }

  /**
   * Check if navigation to URL should be allowed
   */
  shouldAllowNavigation(url: string, source: NavigationSource): boolean {
    const current = this.currentState;

    // Always allow if idle
    if (current.phase === NavigationPhase.IDLE) {
      return true;
    }

    // Allow if previous navigation failed
    if (current.phase === NavigationPhase.ERROR) {
      return true;
    }

    // Same URL navigation
    if (this.lastSyncedUrl === url) {
      return false;
    }

    // Check lock status
    if (this.isLocked) {
      const timeSinceLock = Date.now() - current.timestamp;

      // If same source and within lock period, reject
      if (current.source === source && timeSinceLock < this.LOCK_DURATION) {
        return false;
      }

      // If locked too long (>2s), allow (assume something went wrong)
      if (timeSinceLock > 2000) {
        // Debug logs
        this.warn('Lock expired (>2s), allowing navigation');
        return true;
      }
    }

    return true;
  }

  private updateState(newState: NavigationState): void {
    this.stateSubject.next(newState);
  }

  private setNavigationLock(lockId: string): void {
    this.clearNavigationLock();

    this.navigationLockTimeout = setTimeout(() => {
      if (this.currentState.lockId === lockId) {
        this.releaseLock();
      }
    }, this.LOCK_DURATION);
  }

  private releaseLock(): void {
    this.clearNavigationLock();
    const current = this.currentState;
    if (current.lockId) {
      this.updateState({
        ...current,
        lockId: null,
      });
    }
  }

  private clearNavigationLock(): void {
    if (this.navigationLockTimeout) {
      clearTimeout(this.navigationLockTimeout);
      this.navigationLockTimeout = null;
    }
  }

  private generateLockId(): string {
    return `nav_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Debug logs
  // Logging methods with console grouping for better debugging
  private log(message: string, ...args: unknown[]): void {
    console.log(`${this.LOG_PREFIX} ${message}`, ...args);
  }

  private warn(message: string, ...args: unknown[]): void {
    console.warn(`${this.LOG_PREFIX} ⚠️ ${message}`, ...args);
  }

  private error(message: string, ...args: unknown[]): void {
    console.error(`${this.LOG_PREFIX} ❌ ${message}`, ...args);
  }
}
